import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/layout/SiteChrome";
import { useAuth } from "../store/authStore";
import { getMyOrders } from "../services/api";

const statusColors: Record<string, { bg: string; text: string; border: string }> = {
  "Pending":    { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  "Picked Up":  { bg: "#dbeafe", text: "#1e40af", border: "#93c5fd" },
  "Visited":    { bg: "#e0e7ff", text: "#3730a3", border: "#a5b4fc" },
  "Inspected":  { bg: "#fef3c7", text: "#92400e", border: "#fcd34d" },
  "Offer Made": { bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
  "Completed":  { bg: "#d1fae5", text: "#065f46", border: "#34d399" },
  "Closed":     { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db" },
  "Cancelled":  { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5" },
};

export default function MyOrdersPage() {
  const { isLoggedIn, loaded } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loaded && !isLoggedIn) {
      nav("/login?redirect=/my-orders", { replace: true });
      return;
    }
    if (!isLoggedIn) return;

    setLoading(true);
    getMyOrders()
      .then(data => setOrders(data.orders || []))
      .catch(e => setError(e.message || "Failed to load orders"))
      .finally(() => setLoading(false));
  }, [isLoggedIn, loaded, nav]);

  return (
    <div className="page-bg">
      <Header />

      <section style={{ maxWidth: 800, margin: "0 auto", padding: "clamp(24px,5vw,48px) 20px 72px" }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--text2)", textDecoration: "none", marginBottom: 20, transition: "color 0.2s" }}
            onMouseEnter={e => (e.currentTarget.style.color = "var(--brand)")}
            onMouseLeave={e => (e.currentTarget.style.color = "var(--text2)")}>
            ← Back to Home
          </Link>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(24px,4vw,32px)", color: "var(--text)", letterSpacing: "-0.5px" }}>
            My Orders
          </h1>
          <p style={{ fontSize: 14, color: "var(--text2)", marginTop: 6 }}>
            Track all your device selling orders in one place.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--text2)" }}>
            <div style={{ fontSize: 36, marginBottom: 12, animation: "spin 1s linear infinite" }}>⏳</div>
            <div style={{ fontWeight: 600 }}>Loading your orders...</div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{ textAlign: "center", padding: "48px 20px", background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 16 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>{error}</div>
            <button onClick={() => window.location.reload()} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && orders.length === 0 && (
          <div style={{
            textAlign: "center", padding: "64px 24px", background: "var(--surface)",
            border: "1.5px dashed var(--border)", borderRadius: 20,
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 20, color: "var(--text)", marginBottom: 8 }}>
              No Orders Yet
            </h2>
            <p style={{ fontSize: 14, color: "var(--text2)", marginBottom: 24, maxWidth: 360, margin: "0 auto 24px", lineHeight: 1.6 }}>
              You haven't placed any selling orders yet. Start by selecting your device and get an instant quote.
            </p>
            <Link to="/" style={{
              display: "inline-block", background: "linear-gradient(135deg, var(--brand), var(--brand-dark))",
              color: "#fff", borderRadius: 12, padding: "13px 30px", fontWeight: 700, fontSize: 15,
              fontFamily: "var(--font-display)", textDecoration: "none",
              boxShadow: "0 4px 14px rgba(255,107,53,0.35)", transition: "all 0.2s",
            }}
              onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.transform = ""; }}>
              Start Selling →
            </Link>
          </div>
        )}

        {/* Order list */}
        {!loading && !error && orders.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {orders.map((order: any, i: number) => {
              const status = order.status || "Pending";
              const colors = statusColors[status] || statusColors["Pending"];
              const date = order.scheduledDate
                ? new Date(order.scheduledDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                : "—";
              const created = order.createdAt
                ? new Date(order.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                : "";

              return (
                <div key={order._id || i} className="animate-fade-up"
                  style={{
                    background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 16,
                    padding: "20px 24px", transition: "all 0.25s", animationDelay: `${i * 60}ms`,
                  }}
                  onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--border-bright)"; el.style.boxShadow = "var(--shadow-md)"; }}
                  onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = "var(--border)"; el.style.boxShadow = "none"; }}>

                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    {/* Left: device info */}
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 20 }}>📱</span>
                        <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--text)" }}>
                          {order.deviceName || "Unknown Device"}
                        </h3>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", fontSize: 12, color: "var(--text3)" }}>
                        {order.referenceNo && <span>Ref: <strong style={{ color: "var(--text2)" }}>{order.referenceNo}</strong></span>}
                        {created && <span>Ordered: {created}</span>}
                        {date !== "—" && <span>Pickup: {date}</span>}
                      </div>
                    </div>

                    {/* Right: price + status */}
                    <div style={{ display: "flex", alignItems: "center", gap: 16, flexShrink: 0 }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 11, color: "var(--text3)", textTransform: "uppercase", fontWeight: 600, letterSpacing: "0.06em", marginBottom: 2 }}>Quoted Price</div>
                        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "var(--text)" }}>
                          ₹{(order.quotedPrice || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <div style={{
                        background: colors.bg, color: colors.text, border: `1px solid ${colors.border}`,
                        borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                      }}>
                        {status}
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  {order.timeline && order.timeline.length > 0 && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)", display: "flex", gap: 16, flexWrap: "wrap" }}>
                      {order.timeline.slice(-3).map((t: any, j: number) => (
                        <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text3)" }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: "50%",
                            background: j === order.timeline.length - 1 ? "var(--brand)" : "var(--border-bright)",
                          }} />
                          <span style={{ fontWeight: 600, color: "var(--text2)" }}>{t.status}</span>
                          {t.note && <span>— {t.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
